<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import {
  ArrowTopRightOnSquareIcon,
  BellIcon,
  CheckCircleIcon,
  CheckIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  FireIcon,
  InboxIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline'
import { useActionItemStore, type ActionItem, type ActionPriority } from '@/stores/useActionItemStore'
import { useUserNotificationStore } from '@/stores/useUserNotificationStore'

const router = useRouter()
const actionStore = useActionItemStore()
const notificationStore = useUserNotificationStore()
const { items, count, isLoading } = storeToRefs(actionStore)
const { notifications, isLoading: notifLoading } = storeToRefs(notificationStore)

const tab = ref<'todo' | 'activity'>('todo')
const notifLoaded = ref(false)

onMounted(() => {
  actionStore.fetchActions()
  actionStore.fetchCount()
})

watch(tab, (t) => {
  if (t === 'activity' && !notifLoaded.value) {
    notifLoaded.value = true
    notificationStore.fetchNotifications({})
  }
})

const PRIORITY_UI: Record<ActionPriority, { label: string; badge: string; chip: string; icon: typeof FireIcon }> = {
  URGENT: { label: 'Urgent', badge: 'badge-error', chip: 'bg-error/10 text-error', icon: FireIcon },
  HIGH: { label: 'Important', badge: 'badge-warning', chip: 'bg-warning/15 text-warning', icon: ExclamationTriangleIcon },
  NORMAL: { label: 'Normal', badge: 'badge-info', chip: 'bg-info/10 text-info', icon: CheckCircleIcon },
}
const priorityUi = (p: ActionPriority) => PRIORITY_UI[p] ?? PRIORITY_UI.NORMAL

const subtitle = computed(() => {
  const c = count.value
  if (!c.total) return 'Vous êtes à jour.'
  const parts: string[] = [`${c.total} à faire`]
  if (c.urgent) parts.push(`${c.urgent} urgente${c.urgent > 1 ? 's' : ''}`)
  return parts.join(' · ')
})

function formatAge(iso: string): string {
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (min < 1) return "à l'instant"
  if (min < 60) return `il y a ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `il y a ${h} h`
  return `il y a ${Math.floor(h / 24)} j`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function runAction(item: ActionItem) {
  const route = item.cta?.route
  if (!route) return
  router.push({ path: route, query: (item.cta?.params as Record<string, string>) ?? {} })
}

function authorLabel(n: any): string | null {
  const a = n?.notification?.author
  if (!a) return null
  return [a.firstName, a.lastName].filter(Boolean).join(' ') || null
}
</script>

<template>
  <div class="mx-auto w-full max-w-5xl p-4 md:p-8">
    <header class="mb-6">
      <h1 class="flex items-center gap-2 text-2xl font-bold">
        <ClipboardDocumentCheckIcon class="h-6 w-6 text-primary" aria-hidden="true" />
        Mes actions
      </h1>
      <p class="mt-1 text-base-content/70">{{ subtitle }}</p>
    </header>

    <div class="mb-5 flex items-center gap-2" role="tablist">
      <button
        type="button"
        role="tab"
        class="rounded-full border px-4 py-1.5 text-sm font-medium transition-colors"
        :class="tab === 'todo' ? 'border-secondary bg-secondary text-secondary-content' : 'border-base-300 bg-base-100 text-base-content/70 hover:border-secondary/50'"
        :aria-selected="tab === 'todo'"
        @click="tab = 'todo'"
      >
        À faire{{ count.total ? ` (${count.total})` : '' }}
      </button>
      <button
        type="button"
        role="tab"
        class="rounded-full border px-4 py-1.5 text-sm font-medium transition-colors"
        :class="tab === 'activity' ? 'border-secondary bg-secondary text-secondary-content' : 'border-base-300 bg-base-100 text-base-content/70 hover:border-secondary/50'"
        :aria-selected="tab === 'activity'"
        @click="tab = 'activity'"
      >
        Activité
      </button>
    </div>

    <!-- À faire -->
    <section v-if="tab === 'todo'">
      <div v-if="isLoading" class="flex justify-center py-16">
        <span class="loading loading-spinner loading-lg"></span>
      </div>

      <div
        v-else-if="!items.length"
        class="flex flex-col items-center gap-3 rounded-xl border border-base-200 bg-base-100 py-16 text-center shadow-sm"
      >
        <CheckCircleIcon class="h-12 w-12 text-success/70" aria-hidden="true" />
        <p class="text-base-content/70">Rien à traiter — vous êtes à jour.</p>
      </div>

      <ul v-else class="flex flex-col gap-3">
        <li
          v-for="item in items"
          :key="item.uuid"
          class="card border border-base-300 bg-base-100 shadow-sm"
        >
          <div class="card-body flex-row items-start gap-4 p-4 md:p-5">
            <span
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
              :class="priorityUi(item.effectivePriority).chip"
            >
              <component :is="priorityUi(item.effectivePriority).icon" class="h-5 w-5" aria-hidden="true" />
            </span>

            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <h2 class="text-base font-semibold">{{ item.title }}</h2>
                <span class="badge badge-sm" :class="priorityUi(item.effectivePriority).badge">
                  {{ priorityUi(item.effectivePriority).label }}
                </span>
              </div>
              <p v-if="item.description" class="mt-1 text-base-content/80">{{ item.description }}</p>
              <p class="mt-1.5 text-sm text-base-content/60">{{ formatAge(item.openedAt) }}</p>
            </div>

            <button
              v-if="item.cta?.route"
              type="button"
              class="btn btn-primary btn-sm shrink-0"
              @click="runAction(item)"
            >
              {{ item.cta?.label || 'Traiter' }}
            </button>
          </div>
        </li>
      </ul>
    </section>

    <!-- Activité (notifications FYI) -->
    <section v-else>
      <div v-if="notifLoading" class="flex justify-center py-16">
        <span class="loading loading-spinner loading-lg"></span>
      </div>

      <div
        v-else-if="!notifications.length"
        class="flex flex-col items-center gap-3 rounded-xl border border-base-200 bg-base-100 py-16 text-center shadow-sm"
      >
        <InboxIcon class="h-12 w-12 text-base-content/30" aria-hidden="true" />
        <p class="text-base-content/70">Aucune notification.</p>
      </div>

      <ul v-else class="flex flex-col gap-2">
        <li
          v-for="n in notifications"
          :key="n.notificationUuid"
          class="card border border-base-300 bg-base-100 shadow-sm"
          :class="{ 'ring-1 ring-primary/30': n.status === 'NEW' }"
        >
          <div class="card-body flex-row items-start gap-3 p-3 md:p-4">
            <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-base-200 text-base-content/60">
              <BellIcon class="h-5 w-5" aria-hidden="true" />
            </span>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <h2 class="truncate font-semibold" :class="{ 'text-primary': n.status === 'NEW' }">
                  {{ n.notification.title }}
                </h2>
                <span v-if="authorLabel(n)" class="shrink-0 text-sm text-base-content/50">par {{ authorLabel(n) }}</span>
              </div>
              <p class="mt-0.5 text-base-content/80">{{ n.notification.description }}</p>
              <p class="mt-1 text-sm text-base-content/50">{{ formatDate(n.notification.createdAt) }}</p>
            </div>
            <div class="flex shrink-0 items-center gap-1">
              <button
                v-if="n.status === 'NEW'"
                type="button"
                class="btn btn-ghost btn-xs"
                title="Marquer comme lu"
                @click="notificationStore.markAsRead(n.notificationUuid)"
              >
                <CheckIcon class="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                class="btn btn-ghost btn-xs text-error"
                title="Archiver"
                @click="notificationStore.archiveNotification(n.notificationUuid)"
              >
                <TrashIcon class="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        </li>
      </ul>

      <div class="mt-4 text-center">
        <RouterLink to="/notifications" class="link link-hover text-sm text-base-content/60">
          <ArrowTopRightOnSquareIcon class="mr-1 inline h-4 w-4 align-text-bottom" aria-hidden="true" />
          Voir toutes mes notifications
        </RouterLink>
      </div>
    </section>
  </div>
</template>
